"use client";

import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useHouseholdContext } from "@/contexts/HouseholdContext";
import { useTranslations } from "@/contexts/I18nContext";
import { useFirestoreDoc } from "@/hooks/useFirestore";
import { Avatar } from "@/components/ui/Avatar";
import { LinkPreviewCard } from "@/components/ui/LinkPreviewCard";
import { ReactionBar } from "@/components/ui/ReactionBar";
import { DownloadableImage } from "@/components/ui/DownloadableImage";
import { MentionText } from "@/components/ui/MentionText";
import { ReactionDetailsSheet } from "@/components/ui/ReactionDetailsSheet";
import { fromTimestamp, toTimestamp, updateDocument } from "@/lib/firestore";
import { markChatAsRead } from "@/lib/chatUtils";
import { toggleReaction } from "@/lib/chatReactions";
import { db, storage } from "@/lib/firebase";
import {
  collection,
  addDoc,
  doc,
  onSnapshot,
  query,
  orderBy,
  updateDoc,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import type { HouseholdChat, ChatReply } from "@/lib/models";
import { Link } from "react-router-dom";

interface ChatDetailProps {
  chatId: string;
}

/** Format a Date as a short time string, e.g. "2:34 PM" */
function formatTime(date: Date): string {
  return new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(date);
}

/** Return a human-friendly day label for date separators */
function dayLabel(date: Date): string {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const target = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diffDays = Math.floor(
    (today.getTime() - target.getTime()) / (1000 * 60 * 60 * 24)
  );

  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: now.getFullYear() !== date.getFullYear() ? "numeric" : undefined,
  }).format(date);
}

/** Return a date key string for grouping (YYYY-MM-DD) */
function dateKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

interface ReplyGroup {
  key: string;
  label: string;
  replies: ChatReply[];
}

async function uploadChatImage(
  file: File,
  householdId: string,
  chatId: string
): Promise<string> {
  const { compressForContent } = await import("@/lib/imageCompressor");
  const compressed = await compressForContent(file);
  const fileName = crypto.randomUUID();
  const imageRef = ref(
    storage,
    `chats/${householdId}/${chatId}/${fileName}.jpg`
  );
  await uploadBytes(imageRef, compressed);
  return await getDownloadURL(imageRef);
}

export function ChatDetail({ chatId }: ChatDetailProps) {
  const t = useTranslations();
  const { householdId, members, profile } = useHouseholdContext();
  const [messageText, setMessageText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [replies, setReplies] = useState<ChatReply[]>([]);
  const [repliesLoading, setRepliesLoading] = useState(true);
  const [showNewMessageButton, setShowNewMessageButton] = useState(false);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  // Wave O.1: which reply currently has its reaction picker open. iOS uses
  // long-press; on web we use a hover-revealed "+" button per bubble.
  const [reactionPickerReplyId, setReactionPickerReplyId] = useState<
    string | null
  >(null);
  const [reactorsForReply, setReactorsForReply] = useState<{
    replyId: string;
    reactions: Record<string, string[]>;
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isNearBottomRef = useRef(true);
  const prevRepliesCountRef = useRef(0);

  const chatPath = householdId ? `households/${householdId}/chats` : null;
  const { data: chat, loading: chatLoading } =
    useFirestoreDoc<HouseholdChat>(chatPath, chatId);

  // Check if user is near the bottom of the scroll container
  const checkIfNearBottom = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return true;
    const threshold = 100;
    return (
      container.scrollHeight - container.scrollTop - container.clientHeight <
      threshold
    );
  }, []);

  // Handle scroll events
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const nearBottom = checkIfNearBottom();
      isNearBottomRef.current = nearBottom;
      if (nearBottom) {
        setShowNewMessageButton(false);
      }
    };

    container.addEventListener("scroll", handleScroll, { passive: true });
    return () => container.removeEventListener("scroll", handleScroll);
  }, [checkIfNearBottom]);

  // Real-time replies via onSnapshot
  useEffect(() => {
    if (!householdId || !chatId) return;

    const repliesRef = collection(
      db,
      `households/${householdId}/chats/${chatId}/replies`
    );
    const q = query(repliesRef, orderBy("created_at", "asc"));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const docs = snapshot.docs.map(
          (d) => ({ ...d.data(), uid: d.id }) as ChatReply
        );
        setReplies(docs);
        setRepliesLoading(false);
      },
      (err) => {
        console.error("Failed to listen to replies:", err);
        setRepliesLoading(false);
      }
    );

    return unsubscribe;
  }, [householdId, chatId]);

  // Smart auto-scroll: only scroll if user is near bottom, otherwise show button
  useEffect(() => {
    if (replies.length === 0) return;

    if (isNearBottomRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    } else if (replies.length > prevRepliesCountRef.current) {
      // New messages arrived while scrolled up
      setShowNewMessageButton(true);
    }
    prevRepliesCountRef.current = replies.length;
  }, [replies]);

  // Mark chat as read whenever the chat is open AND new replies arrive.
  // Re-running on `replies.length` ensures incoming messages don't leave
  // the chat looking unread on the sender's other devices. Mirrors iOS
  // ChatDetailView re-mark on snapshot updates.
  useEffect(() => {
    if (!householdId || !chatId || !profile?.uid) return;
    markChatAsRead(householdId, chatId, profile.uid).catch((err) =>
      console.error("Failed to mark chat as read:", err)
    );
  }, [householdId, chatId, profile?.uid, replies.length]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    setShowNewMessageButton(false);
  }, []);

  const findMember = (uid: string) => members.find((m) => m.uid === uid);

  // Group replies by calendar day
  const replyGroups: ReplyGroup[] = useMemo(() => {
    const groups: ReplyGroup[] = [];
    let currentKey = "";

    for (const reply of replies) {
      const date = fromTimestamp(reply.created_at);
      if (!date) continue;
      const key = dateKey(date);
      if (key !== currentKey) {
        currentKey = key;
        groups.push({ key, label: dayLabel(date), replies: [] });
      }
      groups[groups.length - 1].replies.push(reply);
    }

    return groups;
  }, [replies]);

  // Find the last message sent by the current user that has been read by someone else
  const lastSeenReplyId = useMemo(() => {
    if (!profile?.uid) return null;
    for (let i = replies.length - 1; i >= 0; i--) {
      const reply = replies[i];
      if (reply.created_by === profile.uid) {
        const readBy = reply.read_by ?? [];
        const othersWhoRead = readBy.filter((uid) => uid !== profile.uid);
        if (othersWhoRead.length > 0) return reply.uid;
        break; // Only check the last message by current user
      }
    }
    return null;
  }, [replies, profile?.uid]);

  const isGroupChat = (chat?.chat_members.length ?? 0) > 2;

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSelectedImage(file);
    setImagePreviewUrl(URL.createObjectURL(file));
  };

  const clearSelectedImage = () => {
    setSelectedImage(null);
    if (imagePreviewUrl) {
      URL.revokeObjectURL(imagePreviewUrl);
    }
    setImagePreviewUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    const hasText = messageText.trim().length > 0;
    const hasImage = !!selectedImage;

    if ((!hasText && !hasImage) || !householdId || !chatId || !profile?.uid || submitting)
      return;

    setSubmitting(true);
    try {
      let imageUrl: string | undefined;

      // Upload image if selected
      if (selectedImage) {
        imageUrl = await uploadChatImage(selectedImage, householdId, chatId);
      }

      const repliesRef = collection(
        db,
        `households/${householdId}/chats/${chatId}/replies`
      );

      const replyData: Record<string, unknown> = {
        created_by: profile.uid,
        created_at: toTimestamp(new Date()),
        text: messageText.trim(),
        read_by: [profile.uid],
      };

      if (imageUrl) {
        replyData.imageUrl = imageUrl;
      }

      await addDoc(repliesRef, replyData);

      const lastReplyText = hasText ? messageText.trim() : t("chats.sentImage");

      await updateDocument(`households/${householdId}/chats`, chatId, {
        last_reply_uid: profile.uid,
        last_reply_text: lastReplyText,
        last_reply_at: toTimestamp(new Date()),
        last_reply_read_by: [profile.uid],
      });

      setMessageText("");
      clearSelectedImage();
    } catch (error) {
      console.error("Failed to send message:", error);
    } finally {
      setSubmitting(false);
    }
  };

  // Wave O.1: persist a reaction toggle to Firestore. Mirrors iOS
  // `LocalDataStore.toggleChatReaction` — reads the current reactions,
  // computes the new map via the pure helper, and writes the whole map
  // back to `households/{hh}/chats/{chatId}/replies/{replyId}.reactions`.
  const handleToggleReaction = useCallback(
    async (reply: ChatReply, emojiKey: string) => {
      if (!householdId || !chatId || !profile?.uid || !reply.uid) return;
      const nextReactions = toggleReaction(
        reply.reactions,
        emojiKey,
        profile.uid,
      );
      const replyRef = doc(
        db,
        `households/${householdId}/chats/${chatId}/replies/${reply.uid}`,
      );
      try {
        await updateDoc(replyRef, { reactions: nextReactions });
      } catch (error) {
        console.error("Failed to toggle reaction:", error);
      }
    },
    [householdId, chatId, profile?.uid],
  );

  const chatTitle = useMemo(() => {
    if (!chat) return t("chats.fallbackTitle");
    return chat.chat_members
      .map((id) => {
        const m = findMember(id);
        return m?.nickname ?? m?.name ?? "";
      })
      .filter(Boolean)
      .join(", ");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chat, members]);

  if (chatLoading || repliesLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div
          className="w-8 h-8 border-3 border-t-transparent rounded-full animate-spin"
          style={{
            borderColor: "var(--accent-color, #00C7BE)",
            borderTopColor: "transparent",
          }}
        />
      </div>
    );
  }

  if (!chat) {
    return (
      <div className="p-4">
        <p className="text-gray-500">{t("chats.detail.notFound")}</p>
        <Link
          to="/chats"
          className="text-blue-500 text-sm mt-2 inline-block"
        >
          {t("chats.detail.backToChats")}
        </Link>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)] sm:h-[calc(100vh-0px)]">
      {/* Header */}
      <div className="p-4 border-b border-gray-100 dark:border-white/10 shrink-0">
        <div className="flex items-center gap-3">
          <Link
            to="/chats"
            className="text-blue-500 shrink-0"
            aria-label={t("chats.detail.backAria")}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </Link>
          <div className="flex -space-x-2 shrink-0">
            {chat.chat_members.slice(0, 4).map((memberId) => {
              const m = findMember(memberId);
              return (
                <Avatar
                  key={memberId}
                  src={m?.profile_url}
                  alt={m?.nickname ?? m?.name ?? ""}
                  size={28}
                  className="ring-2 ring-white dark:ring-gray-900"
                />
              );
            })}
          </div>
          <p className="font-semibold text-sm truncate">{chatTitle}</p>
        </div>
      </div>

      {/* Messages */}
      <div
        ref={scrollContainerRef}
        className="flex-1 overflow-y-auto p-4 space-y-3 relative"
      >
        {replyGroups.map((group) => (
          <div key={group.key}>
            {/* Date separator */}
            <div className="flex items-center gap-3 my-4">
              <div className="flex-1 h-px bg-gray-200 dark:bg-white/10" />
              <span className="text-xs text-gray-400 whitespace-nowrap">
                {group.label}
              </span>
              <div className="flex-1 h-px bg-gray-200 dark:bg-white/10" />
            </div>

            {/* Messages in this day group */}
            <div className="space-y-0.5">
              {group.replies.map((reply, idx) => {
                const isMe = reply.created_by === profile?.uid;
                const replyAuthor = findMember(reply.created_by);
                const replyDate = fromTimestamp(reply.created_at);
                const showSeenReceipt = reply.uid === lastSeenReplyId;

                // Message grouping: same sender within 2 minutes
                const prevReply = idx > 0 ? group.replies[idx - 1] : null;
                const nextReply = idx < group.replies.length - 1 ? group.replies[idx + 1] : null;
                const prevDate = prevReply ? fromTimestamp(prevReply.created_at) : null;
                const nextDate = nextReply ? fromTimestamp(nextReply.created_at) : null;
                const isSameGroupAsPrev = prevReply
                  && prevReply.created_by === reply.created_by
                  && prevDate && replyDate
                  && (replyDate.getTime() - prevDate.getTime()) < 120000;
                const isSameGroupAsNext = nextReply
                  && nextReply.created_by === reply.created_by
                  && nextDate && replyDate
                  && (nextDate.getTime() - replyDate.getTime()) < 120000;

                const showAvatar = !isMe && !isSameGroupAsPrev;
                const showName = !isMe && !isSameGroupAsPrev;
                const showTimestamp = !isSameGroupAsNext;

                // Count others who read this message
                const othersWhoRead = (reply.read_by ?? []).filter(
                  (uid) => uid !== profile?.uid
                );

                // Bubble corner radius based on grouping position
                const getBubbleClasses = () => {
                  const base = isMe
                    ? "bg-[color:var(--accent-color)] text-white"
                    : "bg-gray-100 dark:bg-white/10 text-gray-800 dark:text-gray-200";

                  if (isMe) {
                    if (isSameGroupAsPrev && isSameGroupAsNext) return `${base} rounded-2xl rounded-r-md`;
                    if (isSameGroupAsPrev) return `${base} rounded-2xl rounded-br-md rounded-tr-md`;
                    if (isSameGroupAsNext) return `${base} rounded-2xl rounded-tr-md`;
                    return `${base} rounded-2xl rounded-br-md`;
                  } else {
                    if (isSameGroupAsPrev && isSameGroupAsNext) return `${base} rounded-2xl rounded-l-md`;
                    if (isSameGroupAsPrev) return `${base} rounded-2xl rounded-bl-md rounded-tl-md`;
                    if (isSameGroupAsNext) return `${base} rounded-2xl rounded-tl-md`;
                    return `${base} rounded-2xl rounded-bl-md`;
                  }
                };

                return (
                  <div key={reply.uid} className={isSameGroupAsPrev ? "mt-0.5" : "mt-3"}>
                    <div
                      className={`flex items-end gap-2 ${
                        isMe ? "justify-end" : "justify-start"
                      }`}
                    >
                      {!isMe && (
                        <div className="w-6 shrink-0">
                          {showAvatar && (
                            <Avatar
                              src={replyAuthor?.profile_url}
                              alt={
                                replyAuthor?.nickname ?? replyAuthor?.name ?? ""
                              }
                              size={24}
                            />
                          )}
                        </div>
                      )}
                      <div
                        className={`max-w-[75%] ${isMe ? "items-end" : "items-start"}`}
                      >
                        {showName && (
                          <p className="text-xs text-gray-400 mb-0.5 px-3">
                            {replyAuthor?.nickname ?? replyAuthor?.name ?? ""}
                          </p>
                        )}
                        {/*
                         * Wave O.1: hover-revealed "+" reaction trigger.
                         * Mirrors iOS long-press affordance — desktop users
                         * hover, touch users tap the button when visible.
                         */}
                        <div
                          className={`group/bubble relative flex items-center gap-1 ${
                            isMe ? "flex-row-reverse" : "flex-row"
                          }`}
                        >
                          <div className={`px-4 py-2 text-sm ${getBubbleClasses()}`}>
                            {/* Image display with hover-revealed download */}
                            {reply.imageUrl && (
                              <DownloadableImage
                                src={reply.imageUrl}
                                alt={t("chats.detail.sharedImageAlt")}
                                className="max-w-60 max-h-44 rounded-lg overflow-hidden mb-1"
                              />
                            )}
                            {reply.text && (
                              <MentionText
                                text={reply.text}
                                members={members}
                                className="whitespace-pre-wrap"
                              />
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() =>
                              setReactionPickerReplyId(
                                reactionPickerReplyId === reply.uid
                                  ? null
                                  : (reply.uid ?? null),
                              )
                            }
                            className="opacity-0 group-hover/bubble:opacity-100 focus:opacity-100 transition-opacity w-7 h-7 shrink-0 rounded-full bg-gray-100 dark:bg-white/10 text-gray-500 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-white/15 flex items-center justify-center text-sm"
                            aria-label={t("chats.reactions.aria.openPicker")}
                          >
                            +
                          </button>
                        </div>
                        {/* Reaction picker popover (Wave O.1) */}
                        {reactionPickerReplyId === reply.uid && (
                          <>
                            <div
                              className="fixed inset-0 z-10"
                              onClick={() => setReactionPickerReplyId(null)}
                            />
                            <div
                              className={`relative z-20 mt-1 ${isMe ? "ml-auto" : ""}`}
                            >
                              <ReactionBar
                                reactions={reply.reactions}
                                currentUserId={profile?.uid}
                                onToggleReaction={(emoji) => {
                                  setReactionPickerReplyId(null);
                                  void handleToggleReaction(reply, emoji);
                                }}
                                addReactionAriaLabel={t("chats.reactions.add")}
                                reactionLabels={{
                                  love: t("posts.reactions.love"),
                                  haha: t("posts.reactions.haha"),
                                  celebrate: t("posts.reactions.celebrate"),
                                  wow: t("posts.reactions.wow"),
                                  sad: t("posts.reactions.sad"),
                                  fire: t("posts.reactions.fire"),
                                }}
                              />
                            </div>
                          </>
                        )}
                        {/* Reaction pills below bubble (Wave O.1) */}
                        {reply.reactions &&
                          Object.keys(reply.reactions).length > 0 &&
                          reactionPickerReplyId !== reply.uid && (
                            <div className={`mt-1 ${isMe ? "ml-auto" : ""}`}>
                              <ReactionBar
                                reactions={reply.reactions}
                                currentUserId={profile?.uid}
                                onToggleReaction={(emoji) =>
                                  void handleToggleReaction(reply, emoji)
                                }
                                onShowReactors={() =>
                                  setReactorsForReply({
                                    replyId: reply.uid ?? "",
                                    reactions: reply.reactions ?? {},
                                  })
                                }
                                showReactorsAriaLabel={t("posts.reactions.viewWho")}
                                addReactionAriaLabel={t("chats.reactions.add")}
                                reactionLabels={{
                                  love: t("posts.reactions.love"),
                                  haha: t("posts.reactions.haha"),
                                  celebrate: t("posts.reactions.celebrate"),
                                  wow: t("posts.reactions.wow"),
                                  sad: t("posts.reactions.sad"),
                                  fire: t("posts.reactions.fire"),
                                }}
                              />
                            </div>
                          )}
                        {/* Link preview below bubble */}
                        {reply.text && (
                          <div className={`max-w-[90%] ${isMe ? "ml-auto" : ""}`}>
                            <LinkPreviewCard text={reply.text} compact />
                          </div>
                        )}
                        {showTimestamp && replyDate && (
                          <p
                            className={`text-xs text-gray-400 mt-0.5 px-3 ${
                              isMe ? "text-right" : "text-left"
                            }`}
                          >
                            {formatTime(replyDate)}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Read receipt */}
                    {showSeenReceipt && isMe && othersWhoRead.length > 0 && (
                      <p className="text-xs text-gray-400 text-right mt-0.5 pr-1">
                        {isGroupChat
                          ? t("chats.detail.readBy", { count: othersWhoRead.length })
                          : t("chats.detail.read")}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />

        {/* New messages floating button */}
        {showNewMessageButton && (
          <button
            onClick={scrollToBottom}
            className="sticky bottom-2 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full bg-[color:var(--accent-color)] text-white text-sm font-semibold shadow-lg transition-opacity hover:opacity-90"
          >
            {t("chats.detail.newMessages")}
          </button>
        )}
      </div>

      {/* Image preview */}
      {imagePreviewUrl && (
        <div className="px-4 pt-2 border-t border-gray-100 dark:border-white/10 bg-white/80 dark:bg-white/5">
          <div className="relative inline-block">
            <img
              src={imagePreviewUrl}
              alt={t("common.selectedImageAlt")}
              className="max-h-24 rounded-lg object-cover"
            />
            <button
              onClick={clearSelectedImage}
              className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-gray-600 text-white text-xs flex items-center justify-center hover:bg-gray-700"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Message Input */}
      <form
        onSubmit={handleSendMessage}
        className="p-4 border-t border-gray-100 dark:border-white/10 bg-white/80 dark:bg-white/5 backdrop-blur-sm shrink-0"
      >
        <div className="flex gap-2 items-center">
          {/* Image picker button */}
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="shrink-0 p-2 rounded-full hover:bg-gray-100 dark:hover:bg-white/10 transition-colors text-gray-500"
            aria-label={t("chats.detail.attachImage")}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <polyline points="21 15 16 10 5 21" />
            </svg>
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageSelect}
            className="hidden"
          />
          <textarea
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            onKeyDown={(e) => {
              // Enter sends; Shift+Enter inserts a newline (web chat
              // convention; mirrors common messaging apps).
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSendMessage(e as unknown as React.FormEvent);
              }
            }}
            placeholder={t("chats.detail.messagePlaceholder")}
            rows={1}
            className="flex-1 px-4 py-2 rounded-2xl bg-gray-100 dark:bg-white/10 text-sm outline-none focus:ring-2 focus:ring-[color:var(--accent-color)] resize-none max-h-32"
          />
          <button
            type="submit"
            disabled={(!messageText.trim() && !selectedImage) || submitting}
            className="px-4 py-2 rounded-full bg-[color:var(--accent-color)] text-white text-sm font-semibold disabled:opacity-50 transition-opacity"
          >
            {submitting ? t("chats.detail.sending") : t("chats.detail.send")}
          </button>
        </div>
      </form>

      {reactorsForReply && (
        <ReactionDetailsSheet
          reactions={reactorsForReply.reactions}
          members={members}
          onClose={() => setReactorsForReply(null)}
        />
      )}
    </div>
  );
}
